import React, { useState, useContext } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion.js";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CommonDownload from "../../custom-components/CommonDownload.js";
import { useLocation, useNavigate } from "react-router-dom";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners.js";
import { userStoryToUseCase } from "../../../services/webServices.js";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  TestCaseFlowDataContext,
  TestCaseLoaderStateContext,
} from "../../main-pages/TestcaseGenerationPage";
import RenderTableDataInPDF from "../../../utils/RenderTableDataInPDF.js";
import MessagePopup from "../../popup-components/MessagePopup.js";

const GenerateFSDComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const {setLoadingState} = useContext(LoadingModalContext);
  const { testCaseLoaderState, setTestCaseLoaderState } = useContext(
    TestCaseLoaderStateContext
  );

  const { userStory, functionalSpecDocument, useCase } = useContext(
    TestCaseFlowDataContext
  );

  const defaultFSDExpanded =
    Object.keys(functionalSpecDocument || {}).length > 0 &&
    Object.keys(useCase || {}).length === 0;

  const flattenedFunctionalSpecDocument = {};
  Object.entries(functionalSpecDocument).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      flattenedFunctionalSpecDocument[key] = value;
    } else if (!!value && typeof value === "object") {
      flattenedFunctionalSpecDocument[key] = flattenObject(value);
    } else {
      flattenedFunctionalSpecDocument[key] = value.toString();
    }
  });

  const renderFunctionalRequirementsTable = (requirements) => {
    if (!Array.isArray(requirements)) {
      console.error(
        "Functional Requirements data is not an array:",
        requirements
      );
      return <p>Invalid Functional Requirements data</p>;
    }
    console.log("Table data", requirements);
    return (
      <div>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ marginBottom: 1, fontSize: "14px", marginTop: 3 }}
        >
          Functional Requirements
        </Typography>
        <table
          border="1"
          style={{
            borderCollapse: "collapse",
            width: "100%",
            marginBottom: "30px",
          }}
        >
          <thead
            style={{
              fontSize: "13px",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <tr>
              <th>Requirement ID</th>
              <th>Feature</th>
              <th>Description</th>
              <th>User Story Reference</th>
            </tr>
          </thead>
          <tbody
            style={{
              fontSize: "13px",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            {requirements.map((req, index) => (
              <tr key={index}>
                <td>{req["Requirement ID"]}</td>
                <td>{req["Feature"]}</td>
                <td>{req["Description"]}</td>
                <td>{req["User Story Reference"]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderField = (fieldName, fieldValue) => {

    // Handle "Functional Requirements" separately to render as a table
    if (fieldName === "Functional Requirements") {
      return renderFunctionalRequirementsTable(fieldValue);
    }

    //Handle simple arrays
    if (Array.isArray(fieldValue)) {
      return (
        <div>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ marginTop: 3, marginBottom: 1, fontSize: "14px" }}
          >
            {fieldName}
          </Typography>
          <div>
            {fieldValue.map((item, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "text.primary",
                  display: "block",
                }}
              >
                {`${index + 1}. ${item}`}
              </Typography>
            ))}
          </div>
        </div>
      );
    }

    // Handle primitive values and strings
    if (typeof fieldValue === "string" || typeof fieldValue === "number") {
      const marginTopValue = fieldName === "Overview" ? -1 : 3;
      return (
        <div>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              marginTop: marginTopValue,
              marginBottom: 1,
              fontSize: "14px",
            }}
          >
            {fieldName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "14px" }}
            dangerouslySetInnerHTML={{
              __html: fieldValue.toString().replace(/\n/g, "<br>"),
            }}
          />
        </div>
      );
    }
    return null;
  };

  const generateFormattedDataForDownload = (data) => {
    return Object.entries(data).map(([key, value]) => {
      let formattedValue = "";

      if (Array.isArray(value)) {
        formattedValue = value
          .map((item) =>
            typeof item === "object" && item !== null
              ? Object.entries(item)
                .map(([itemKey, itemValue]) => `${itemKey}: ${itemValue}`)
                .join(", ")
              : item
          )
          .join(", ");
      } else if (typeof value === "object" && value !== null) {
        formattedValue = Object.entries(value)
          .map(([objKey, objValue]) => `${objKey}: ${objValue}`)
          .join(", ");
      } else if (typeof value === "string" || typeof value === "number") {
        formattedValue = value.toString();
      }

      return {
        heading: key,
        type: "text",
        data: formattedValue,
      };
    });
  };

  const formatValueForDoc = (value) => {
    if (Array.isArray(value)) {
      return value.map((item, index) => `${index + 1}. ${item}`).join("\n");
    } else if (typeof value === "string") {
      return value;
    }
    return value;
  };

  const fsdDetails = (
    <Box sx={{ padding: "10px" }}>
      {Object.entries(flattenedFunctionalSpecDocument).map(
        ([fieldName, fieldValue]) => renderField(fieldName, fieldValue)
      )}
    </Box>
  );

  const handleDownload = () => {
    setDownloadOpen(true);
  };

  const wordData = generateFormattedDataForDownload(
    flattenedFunctionalSpecDocument
  );

  const handleGenerateUseCase = async () => {
    let fake;

    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 0.9;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 98) progress = 98;
        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200); 

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);

          // Rush to 100%
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };
    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Use Case",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      setTestCaseLoaderState("Use Cases");

      const response = await userStoryToUseCase({ userStory });

      if (!response?.data || Object.keys(response.data).length === 0) {
        setPopupMessage("No use case data returned from the server.");
        setPopupOpen(true);
        return;
      }
      await finishTo100AndClose();
      navigate("/dashboard/generate-testcases/output", {
        state: { ...location.state, useCase: response.data },
        replace: true,
      });
    } catch (error) {
      console.error("Error generating use case:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        "An error occurred while generating the use case.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setTestCaseLoaderState(null);
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const buttons = [
    {
      label: "Download",
      variant: "text",
      onClick: handleDownload,
      startIcon: <FileDownloadOutlinedIcon />,
      sx: { textTransform: "none" },
    },
    {
      label: "Generate Use Case",
      variant: "contained",
      onClick: handleGenerateUseCase,
      startIcon:
        testCaseLoaderState === "Use Case" ? (
          <CircularProgress size="14px" />
        ) : (
          <GenerateButton />
        ),
      disabled: !!testCaseLoaderState,
    },
  ];
  return (
    <Box width={"601px"} sx={{ height: "100%" }}>
      <CommonAccordion
        summary={"Functional Specification Document"}
        details={fsdDetails}
        buttons={buttons}
        type={"output"}
        defaultExpanded={defaultFSDExpanded}
        fullScreenMode={true}
      />

      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName={"Functional Specification Document"}
        serviceResponse={[
          {
            heading: "Overview",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Overview"]
            ),
          },
          {
            heading: "Scope and Objectives",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Scope and Objectives"]
            ),
          },
          {
            heading: "Functional Requirements",
            type: "table",
            data:
              Object.keys(flattenedFunctionalSpecDocument).length > 0
                ? RenderTableDataInPDF(
                  flattenedFunctionalSpecDocument,
                  "Functional Requirements"
                )
                : "",
          },
          {
            heading: "System Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["System Requirements"]
            ),
          },
          {
            heading: "User Interface Specifications",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["User Interface Specifications"]
            ),
          },
          {
            heading: "Workflows and Processes",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Workflows and Processes"]
            ),
          },
          {
            heading: "Data Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Data Requirements"]
            ),
          },
          {
            heading: "Non-Functional Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Non-Functional Requirements"]
            ),
          },
          {
            heading: "Assumptions and Constraints",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Assumptions and Constraints"]
            ),
          },
          {
            heading: "Acceptance Criteria",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Acceptance Criteria"]
            ),
          },
        ]}

        wordData={[
          {
            heading: "Overview",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Overview"]
            ),
          },
          {
            heading: "Scope and Objectives",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Scope and Objectives"]
            ),
          },
          {
            heading: "Functional Requirements",
            type: "table",
            data:
              Object.keys(flattenedFunctionalSpecDocument).length > 0
                ? RenderTableDataInPDF(
                  flattenedFunctionalSpecDocument,
                  "Functional Requirements"
                )
                : "",
          },
          {
            heading: "System Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["System Requirements"]
            ),
          },
          {
            heading: "User Interface Specifications",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["User Interface Specifications"]
            ),
          },
          {
            heading: "Workflows and Processes",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Workflows and Processes"]
            ),
          },
          {
            heading: "Data Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Data Requirements"]
            ),
          },
          {
            heading: "Non-Functional Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Non-Functional Requirements"]
            ),
          },
          {
            heading: "Assumptions and Constraints",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Assumptions and Constraints"]
            ),
          },
          {
            heading: "Acceptance Criteria",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Acceptance Criteria"]
            ),
          },
        ]}
        documents={["pdf", "docx"]}
      />
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />

    </Box>
  );
};

export default GenerateFSDComponent;
